import { Form, Input, Select, DatePicker } from 'antd';
import { useState, useCallback, useEffect, } from 'react';
import { apiClient, apiErrorMessage } from '../../utils';
import { QaError, NotificationType, ErrorCode } from '../../models/index';
import { ConfirmGroupButton } from '../../elements/confim-group-button';
import { errorTypes } from '../../models/enums/error-type';
import moment from 'moment';

interface IQaErrorEditPage {
  id?: string;
  onSaved: (error?: Error) => void;
  onCancel: () => void
}

export function QaErrorEditPage(props: IQaErrorEditPage) {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [notificationTypes, setNotificationTypes] = useState<(NotificationType & { key: string })[]>();
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [errorCodes, setErrorCodes] = useState<(ErrorCode & { key: string })[]>();
  const [qaError, setQaError] = useState<QaError>();
  const [errorCodeLoading, setErrorCodeLoading] = useState(false);

  useEffect(() => {
    if (!form || !qaError) {
      return;
    }
    form.setFieldsValue({
      ...qaError,
      firstNotificationDate: moment(qaError.firstNotificationDate)
    });
  }, [form, qaError]);

  const loadQaError = useCallback(() => {
    apiClient
      .get(`qa-errors/${props.id}`)
      .then((response: any) => {
        setQaError(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  }, [props.id]);

  useEffect(() => {
    if (!props.id) {
      return;
    }
    loadQaError();
  }, [props.id, loadQaError]);

  const loadNotifications = useCallback(() => {
    setNotificationLoading(true);
    apiClient
      .get(`notifications`)
      .then((response: any) => {
        const responseNotifications: NotificationType[] = response.data;
        const responseData = responseNotifications.map(notification => {
          return {
            key: notification.id,
            ...notification,
          };
        });
        setNotificationTypes(responseData);
        setNotificationLoading(false);
      })
      .catch((reason: any) => {
        setNotificationLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const loadErrorCodes = useCallback(() => {
    setErrorCodeLoading(true);
    apiClient
      .get(`error-codes`)
      .then((response: any) => {
        const responseErrorCode: ErrorCode[] = response.data;
        const responseData = responseErrorCode.map(errorCode => {
          return {
            key: errorCode.id,
            ...errorCode,
          };
        });
        setErrorCodes(responseData);
        setErrorCodeLoading(false);
      })
      .catch((reason: any) => {
        setErrorCodeLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadErrorCodes();
  }, [loadErrorCodes]);

  const onFinish = (values: any) => {
    setLoading(true);
    if (!props.id) {
      apiClient
        .post('qa-errors', values)
        .then(() => {
          props.onSaved();
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      apiClient
        .put(`qa-errors/${props?.id}`, values)
        .then((response: any) => {
          props.onSaved(response.data);
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <Form form={form} layout="vertical" name="basic" onFinish={onFinish} >
      {props.id ?
        <Form.Item
          label="Hata no"
          name="errorNo"
        >
          <Input disabled={true} />
        </Form.Item>
        : null}

      <Form.Item
        label="Bilirim türü"
        rules={[
          {
            required: true,
            message: 'Zorunlu alan',
          },
        ]}
        name="notificationTypeId"
      >
        <Select loading={notificationLoading}>
          {notificationTypes?.map((n: NotificationType) => (
            <Select.Option key={n.id} value={n.id}>
              {n.name} ({errorTypes.get(n.errorType)})
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="İlk bildirim tarihi" name="firstNotificationDate">
        <DatePicker format="DD.MM.YYYY" />
      </Form.Item>

      <Form.Item
        label="Hata kodu"
        rules={[
          {
            required: true,
            message: 'Zorunlu alan',
          },
        ]}
        name="errorCodeId"
      >
        <Select loading={errorCodeLoading}>
          {errorCodes?.map((e: ErrorCode) => (
            <Select.Option key={e.id} value={e.id}>
              {e.code} ({e.description})
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item>
        <ConfirmGroupButton onCancel={props.onCancel} loading={loading} />
      </Form.Item>
    </Form>
  );
}
